import { useTranslation } from "react-i18next";
import { useRef } from "react";
import styled from "styled-components";
import Slider from "react-slick";
import Header from "@/components/main/Header";
import Banner from "@/components/learn/Banner";
import Items from "@/components/learn/Items";
import How, { type THow } from "@/components/learn/How";
import EcoSystem from "@/components/products/detail/EcoSystem";
import TileBanner from "@/components/learn/TileBanner";
import OtherModels from "@/components/products/detail/OtherModels";
import ContactUs from "@/components/contact-us";
import Footer from "@/components/main/Footer";
import { TApplicationData, TEcoSystemGuide } from "@/typings/Props";
import { theme } from "@/styles/theme";
import { media } from "@/styles/media";

export default function Page() {
  const { t } = useTranslation();
  const scrollToContact = useRef<HTMLDivElement | null>(null);
  const items: TApplicationData[] = [
    {
      id: 1,
      title: t("menu.learn.fitness.item.title-1"),
      desc: t("menu.learn.fitness.item.desc-1"),
      iconPath: "/learn/application/Service-Validation.svg",
    },
    {
      id: 2,
      title: t("menu.learn.fitness.item.title-2"),
      desc: t("menu.learn.fitness.item.desc-2"),
      iconPath: "/learn/application/Customer-Retention.svg",
    },
    {
      id: 3,
      title: t("menu.learn.fitness.item.title-3"),
      desc: t("menu.learn.fitness.item.desc-3"),
      iconPath: "/learn/application/Additional-Revenue.svg",
    },
  ];

  const how: THow = {
    title: "menu.learn.fitness.how.title",
    img: "/learn/application/application-fitness-result-section-scaled.webp",
    items: [
      {
        title: "menu.learn.fitness.how.item.title-1",
        desc: "menu.learn.fitness.how.item.desc-1",
      },
      {
        title: "menu.learn.fitness.how.item.title-2",
        desc: "menu.learn.fitness.how.item.desc-2",
      },
      {
        title: "menu.learn.fitness.how.item.title-3",
        desc: "menu.learn.fitness.how.item.desc-3",
      },
      {
        title: "menu.learn.fitness.how.item.title-4",
        desc: "menu.learn.fitness.how.item.desc-4",
      },
      {
        title: "menu.learn.fitness.how.item.title-5",
        desc: "menu.learn.fitness.how.item.desc-5",
      },
    ],
  };

  const ecoSystem: TEcoSystemGuide[] = [
    {
      id: "+",
      title: t("menu.learn.fitness.ecosystem-1.title"),
      desc: t("menu.learn.fitness.ecosystem-1.desc"),
      coord: {
        x: "18.5%",
        y: "10.6%",
      },
    },
    {
      id: "+",
      title: t("menu.learn.fitness.ecosystem-2.title"),
      desc: t("menu.learn.fitness.ecosystem-2.desc"),
      coord: {
        x: "29%",
        y: "38%",
      },
    },
    {
      id: "+",
      title: t("menu.learn.fitness.ecosystem-3.title"),
      desc: t("menu.learn.fitness.ecosystem-3.desc"),
      coord: {
        x: "46%",
        y: "33%",
      },
    },
    {
      id: "+",
      title: t("menu.learn.fitness.ecosystem-4.title"),
      desc: t("menu.learn.fitness.ecosystem-4.desc"),
      coord: {
        x: "44%",
        y: "52%",
      },
    },
    {
      id: "+",
      title: t("menu.learn.fitness.ecosystem-5.title"),
      desc: t("menu.learn.fitness.ecosystem-5.desc"),
      coord: {
        x: "50%",
        y: "74.5%",
      },
    },
    {
      id: "+",
      title: t("menu.learn.fitness.ecosystem-6.title"),
      desc: t("menu.learn.fitness.ecosystem-6.desc"),
      coord: {
        x: "43.8%",
        y: "86.6%",
      },
    },
    {
      id: "+",
      title: t("menu.learn.fitness.ecosystem-7.title"),
      desc: t("menu.learn.fitness.ecosystem-7.desc"),
      coord: {
        x: "61.5%",
        y: "47.9%",
      },
    },
    {
      id: "+",
      title: t("menu.learn.fitness.ecosystem-8.title"),
      desc: t("menu.learn.fitness.ecosystem-8.desc"),
      coord: {
        x: "68.8%",
        y: "65.3%",
      },
    },
    {
      id: "+",
      title: t("menu.learn.fitness.ecosystem-9.title"),
      desc: t("menu.learn.fitness.ecosystem-9.desc"),
      coord: {
        x: "82.4%",
        y: "53.2%",
      },
    },
  ];

  return (
    <>
      <Header />
      <Banner
        Bg="/learn/banner/Rectangle-86-2.png"
        Headline={t("menu.learn.banner.headline")}
        Title={t("menu.learn.fitness.banner.title")}
        Desc={t("menu.learn.fitness.banner.desc")}
      />
      <Items title={t("menu.learn.fitness.item.main-title")} items={items} />

      <How how={how} />

      <YoutubeWrap>
        <YoutubeSlickWrap>
          <Slider
            {...{
              dots: true,
              autoplay: false,
              arrows: false,
              appendDots: (dots) => (
                <div>
                  <ul>{dots}</ul>
                </div>
              ),
            }}
          >
            <IframeWrap>
              <iframe
                loading="lazy"
                title="InBody Spotlight UK - Tim Walker - Evolve Fitness"
                src="https://www.youtube.com/embed/uEH7lmuUthM?feature=oembed&amp;enablejsapi=1&amp;origin=https://inbodyasia.com"
              />
            </IframeWrap>
            <IframeWrap>
              <iframe
                loading="lazy"
                title="InBody Spotlight UK - Olu Adepitan - BXR"
                src="https://www.youtube.com/embed/1DGKwDDuJ5M?feature=oembed&enablejsapi=1&origin=https://inbodyasia.com"
              />
            </IframeWrap>
          </Slider>
        </YoutubeSlickWrap>
        <YoutubeDescWrap>
          <YoutubeDescSubTitle>{t("menu.learn.fitness.youtube.sub-title")}</YoutubeDescSubTitle>
          <YoutubeDescTitle dangerouslySetInnerHTML={{ __html: t("menu.learn.fitness.youtube.title") }} />
        </YoutubeDescWrap>
      </YoutubeWrap>

      <EcoSystemWrap>
        <EcoSystem
          ecoImg={"/learn/application/Health-Corner-380-Fitness-new.webp"}
          title={t("menu.learn.fitness.ecosystem.title")}
          desc={t("menu.learn.fitness.ecosystem.desc")}
          ecoSystem={ecoSystem}
        />
      </EcoSystemWrap>
      <IntegrationsWrap>
        <IntegrationsTitleWrap>
          <IntegrationsSubTitle>{t("menu.learn.fitness.integrated.integrated")}</IntegrationsSubTitle>
          <IntegrationsTitle dangerouslySetInnerHTML={{ __html: t("menu.learn.fitness.integrated.title") }} />
          <IntegrationsDesc>{t("menu.learn.fitness.integrated.desc")}</IntegrationsDesc>
        </IntegrationsTitleWrap>
        <IntegrationItemWrap>
          <IntegrationItem $bg="/learn/application/fitness/Milon.png"></IntegrationItem>
          <IntegrationItem $bg="/learn/application/fitness/TechnoGym.png"></IntegrationItem>
          <IntegrationItem $bg="/learn/application/fitness/eGYM.png"></IntegrationItem>
          <IntegrationItem $bg="/learn/application/fitness/Myzone.png"></IntegrationItem>
        </IntegrationItemWrap>
      </IntegrationsWrap>
      <TileBanner
        backgroundImg={"/learn/application/medical/BookCover.png"}
        backgroundColor="#fff"
        title={t("menu.learn.fitness.download.title")}
        btn={{
          text: t("menu.learn.fitness.download.btn"),
          onClick: () => {
            scrollToContact.current!.scrollIntoView({ behavior: "smooth" });
          },
        }}
        negative
      />
      <OtherModels />
      <div style={{ margin: "0 0 20px 0" }} />
      <div ref={scrollToContact}>
        <ContactUs />
      </div>
      <Footer />
    </>
  );
}

const YoutubeWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 100px 0;
  background-color: ${theme.colors.background.white};

  @media (max-width: 1000px) {
    flex-direction: column;
    padding: 50px 0;
  }
`;
const YoutubeSlickWrap = styled.div`
  width: 35%;
  max-width: 500px;
  height: 300px;

  .slick-dots {
    bottom: -30px;

    ul {
      li {
      }

      li > button {
        display: initial;
      }

      li > button::before {
        content: "";
        width: 6px;
        height: 6px;
        transition: all 0.25s ease;
        border-radius: 50%;
        margin-left: 11px;
        background-color: transparent;
        box-shadow: inset 0 0 0 5px #000;
      }

      li.slick-active > button::before {
        transform: scale(2);
        box-shadow: inset 0 0 0 1px #000;
        background-color: transparent;
      }
    }
  }

  ${media.tablet`
    padding: 0 20px;
    width: 100%;
  `}
`;
const IframeWrap = styled.div`
  padding-top: 56.25%;
  position: relative;
  width: 100%;

  & > iframe {
    width: 100%;
    height: 100%;
    display: block;
    position: absolute;
    margin: 0;
    top: 0;
    left: 0;
    -webkit-box-sizing: border-box;
    -moz-box-sizing: border-box;
    box-sizing: border-box;
    border: none;
    border-radius: 10px;
  }
`;
const YoutubeDescWrap = styled.div`
  padding: 20px 0 50px 50px;
  width: 40%;
  ${media.tablet`
    width: 60%;
    padding: 0 20px;
  `}
  ${media.phablet`
    width: 100%;  
  `}
`;
const YoutubeDescSubTitle = styled.p`
  width: 100%;
  margin: auto;
  margin-bottom: 7px;
  line-height: 32px;
  font-family: Hind;
  font-size: 1.75rem;
  font-weight: ${theme.fontWeights.regular};
  color: ${theme.colors.text.grayDark};
  letter-spacing: 0;
`;
const YoutubeDescTitle = styled(YoutubeDescSubTitle)`
  line-height: 50px;
  font-size: 44px;
  font-weight: ${theme.fontWeights.bold};
`;

const EcoSystemWrap = styled.div`
  background-color: ${theme.colors.background.white};
  margin: 30px 0;
  padding: 30px 0;
`;
const IntegrationsWrap = styled.div`
  padding: 100px 0;
`;
const IntegrationsTitleWrap = styled.div`
  width: 1000px;
  margin: auto;

  @media (max-width: 1180px) {
    width: 100%;
  }
`;
const IntegrationsSubTitle = styled(YoutubeDescSubTitle)`
  text-align: center;
`;
const IntegrationsTitle = styled(YoutubeDescTitle)`
  text-align: center;
  font-weight: 700;
`;
const IntegrationsDesc = styled.p`
  text-align: center;
  line-height: 150%;
  color: ${theme.colors.text.grayLight};
`;
const IntegrationItemWrap = styled.div`
  margin-top: 40px;
  margin-bottom: 50px;
  display: flex;
  justify-content: center;
  gap: 100px;
  ${media.tabletWide`
    flex-wrap: wrap;
  `}
`;

const IntegrationItem = styled.div<{ $bg: string }>`
  width: 214px;
  height: 217px;
  background: url(${({ $bg }) => $bg});
`;
